// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { Link } from 'react-router-dom'
import { GuardRokIcon } from '../components/GuardRokLogo'
import { ShieldCheck, Lock, EyeOff, FileText, Server, Clock, ArrowRight, CheckCircle } from 'lucide-react'

const RETENTION = [
  { plan: 'Free Trial', duration: '7 days', notes: 'Compliance events only · no prompt content' },
  { plan: 'Starter', duration: '90 days', notes: 'Compliance events only · no prompt content' },
  { plan: 'Professional', duration: '1 year', notes: 'Compliance events only · no prompt content' },
  { plan: 'Business', duration: '1 year', notes: 'Compliance events only · no prompt content' },
  { plan: 'Enterprise', duration: '7 years', notes: 'Configurable · SOX & HIPAA compliant' },
]

const PILLARS = [
  {
    icon: EyeOff,
    title: 'Prompt text never stored',
    body: 'GuardRok intercepts prompts in the browser before they are sent to any AI tool. Prompt content is never transmitted to GuardRok\'s servers. We log compliance events — what action was taken and which policy category was triggered — never the content of what was typed.',
  },
  {
    icon: Lock,
    title: 'Zero-Knowledge Identity',
    body: 'GuardRok never learns who your employees are. Every user is identified by a randomly assigned anonymous GR-ID (e.g. GR-4821). Only your HR system holds the mapping from GR-ID to a real name. We physically cannot identify individuals — by design.',
  },
  {
    icon: ShieldCheck,
    title: 'Encryption in transit and at rest',
    body: 'All data in transit is encrypted using current industry-standard protocols. Event logs are encrypted at rest. Access controls enforce strict organisation-level data isolation — one customer cannot access another\'s records under any circumstances.',
  },
  {
    icon: Server,
    title: 'No third-party data sharing',
    body: 'We do not sell, rent, or share your data with advertisers, data brokers, or AI model trainers. Our infrastructure providers (hosting, database, payments) are contractually bound to data protection terms. Full list available on request.',
  },
  {
    icon: FileText,
    title: 'BAA & DPA available',
    body: 'Healthcare organisations requiring a HIPAA Business Associate Agreement (BAA) can request one from hello@guardrok.com — we will return a signed copy within 24 hours. A GDPR-compliant Data Processing Agreement (DPA) is available for EU/UK organisations on the same timeline.',
  },
  {
    icon: Clock,
    title: 'SOC 2 — audit underway',
    body: 'We are currently undergoing our SOC 2 Type I audit. Our security controls, policies, and scope documentation are complete. Organisations that require evidence of our controls before the published report is available can request a security review call — we will walk through our control framework directly.',
  },
]

export default function Security() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#030712',
        color: '#fff',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* Nav */}
      <nav
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '20px 48px',
          borderBottom: '1px solid #0f172a',
          position: 'sticky',
          top: 0,
          background: 'rgba(3,9,18,0.95)',
          backdropFilter: 'blur(12px)',
          zIndex: 100,
        }}
      >
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}
        >
          <GuardRokIcon size={36} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: '-0.5px', color: '#fff' }}>
              Guard<span style={{ color: '#E63946' }}>Rok</span>
              <span style={{ fontSize: 8, color: '#6b7280', verticalAlign: 'super', marginLeft: 1 }}>
                ™
              </span>
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: '3px',
                color: '#6b7280',
                textTransform: 'uppercase',
                marginTop: 2,
              }}
            >
              AI Firewall
            </div>
          </div>
        </Link>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/enterprise" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>
            Enterprise
          </Link>
          <Link to="/pricing" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>
            Pricing
          </Link>
          <a
            href="mailto:hello@guardrok.com"
            style={{
              background: '#0f172a',
              border: '1px solid #1e293b',
              color: '#cbd5e1',
              fontSize: 13,
              fontWeight: 600,
              padding: '8px 16px',
              borderRadius: 8,
              textDecoration: 'none',
            }}
          >
            Security questions?
          </a>
        </div>
      </nav>

      {/* Hero */}
      <div
        style={{ maxWidth: 840, margin: '0 auto', padding: '72px 40px 56px', textAlign: 'center' }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#0f172a',
            border: '1px solid rgba(230,57,70,0.25)',
            borderRadius: 20,
            padding: '5px 14px',
            marginBottom: 24,
          }}
        >
          <ShieldCheck size={12} color="#E63946" />
          <span style={{ fontSize: 12, color: '#E63946', fontWeight: 600 }}>
            Trust Center
          </span>
        </div>
        <h1
          style={{
            fontSize: 48,
            fontWeight: 900,
            letterSpacing: '-2px',
            lineHeight: 1.05,
            marginBottom: 18,
          }}
        >
          Security &amp; Privacy
          <br />
          <span style={{ color: '#E63946' }}>by Architecture</span>
        </h1>
        <p
          style={{
            fontSize: 17,
            color: '#94a3b8',
            maxWidth: 620,
            margin: '0 auto 40px',
            lineHeight: 1.65,
          }}
        >
          GuardRok is a security product. The way we handle your data must meet a higher standard
          than most software. This page explains exactly how we protect it — for procurement
          reviews, security teams, and anyone who needs to know before they buy.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <a
            href="mailto:hello@guardrok.com?subject=Security Review"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#E63946',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              padding: '12px 24px',
              borderRadius: 10,
              textDecoration: 'none',
            }}
          >
            Request a Security Review <ArrowRight size={15} />
          </a>
          <a
            href="mailto:hello@guardrok.com?subject=BAA Request"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#0f172a',
              border: '1px solid #1e293b',
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              padding: '12px 24px',
              borderRadius: 10,
              textDecoration: 'none',
            }}
          >
            Request BAA / DPA
          </a>
        </div>
      </div>

      {/* Quick-glance credential row */}
      <div
        style={{
          borderTop: '1px solid #0f172a',
          borderBottom: '1px solid #0f172a',
          padding: '14px 40px',
        }}
      >
        <div
          style={{
            maxWidth: 1000,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 32,
            flexWrap: 'wrap',
          }}
        >
          {[
            { icon: '🔐', label: 'SOC 2 Type I', sub: 'Audit underway — report on request' },
            { icon: '🏥', label: 'HIPAA BAA', sub: 'Available · signed in 24 hrs' },
            { icon: '🇪🇺', label: 'GDPR DPA', sub: 'Available · EU/UK compliant' },
            { icon: '🔒', label: 'TLS 1.3 + AES-256', sub: 'In transit and at rest' },
            { icon: '🪪', label: 'Zero-Knowledge Identity', sub: 'We never know employee names' },
          ].map(({ icon, label, sub }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>{icon}</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#cbd5e1', letterSpacing: '0.2px' }}>
                  {label}
                </div>
                <div style={{ fontSize: 10, color: '#475569' }}>{sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Six pillars */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '64px 40px' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#E63946',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: 10,
          }}
        >
          How we protect your data
        </div>
        <h2
          style={{
            fontSize: 28,
            fontWeight: 800,
            textAlign: 'center',
            marginBottom: 48,
            letterSpacing: '-0.5px',
          }}
        >
          Six things procurement will ask — answered
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          {PILLARS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              style={{
                background: '#0d1117',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: '28px 28px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: 'rgba(230,57,70,0.1)',
                    border: '1px solid rgba(230,57,70,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon size={16} color="#E63946" />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>{title}</div>
              </div>
              <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.7, margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Zero-Knowledge deep dive */}
      <div
        style={{
          background: '#08111e',
          borderTop: '1px solid #0f172a',
          borderBottom: '1px solid #0f172a',
          padding: '64px 40px',
        }}
      >
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'rgba(230,57,70,0.1)',
                border: '1px solid rgba(230,57,70,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EyeOff size={18} color="#E63946" />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', margin: 0 }}>
              Zero-Knowledge Identity — in plain English
            </h2>
          </div>
          <p style={{ fontSize: 15, color: '#94a3b8', lineHeight: 1.75, marginBottom: 20 }}>
            When an employee installs GuardRok™ and their employer adds them to the account, the
            system assigns a random anonymous identifier — a GR-ID like <code style={{ background: '#0f172a', padding: '2px 6px', borderRadius: 4, color: '#e2e8f0' }}>GR-4821</code>.
            All audit logs, violation reports, and usage analytics reference only this GR-ID.
            GuardRok's servers never receive the employee's name, email address, or any other
            personally identifying information.
          </p>
          <p style={{ fontSize: 15, color: '#94a3b8', lineHeight: 1.75, marginBottom: 24 }}>
            The mapping from GR-ID to real identity lives exclusively in your HR or identity
            system. If you need to investigate a specific violation, your HR team looks up GR-4821
            in your own records. GuardRok cannot assist with that lookup — by design.
          </p>
          <div
            style={{
              background: '#0f172a',
              border: '1px solid #1e293b',
              borderRadius: 12,
              padding: '20px 24px',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: '#E63946', marginBottom: 12, letterSpacing: '1px', textTransform: 'uppercase' }}>
              Why this matters for enterprise buyers
            </div>
            {[
              'GDPR by design — no personal data in our analytics pipeline',
              'Union / works-council friendly — employer holds the key, not the vendor',
              'CISO-level trust — a vendor breach cannot expose employee identities',
              'Investor-grade audit trail without the HR liability',
            ].map(pt => (
              <div key={pt} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                <CheckCircle size={14} color="#22c55e" style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.5 }}>{pt}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data retention table */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '64px 40px' }}>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 800,
            marginBottom: 8,
            letterSpacing: '-0.5px',
          }}
        >
          Data retention by plan
        </h2>
        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 28, lineHeight: 1.6 }}>
          Retention applies to event metadata only — action taken, policy category, timestamp, and anonymous GR-ID.
          Prompt text is never retained at any tier.
        </p>
        <div
          style={{
            background: '#0d1117',
            border: '1px solid #1e293b',
            borderRadius: 12,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 120px 1fr',
              background: '#080f1a',
              padding: '12px 24px',
              borderBottom: '1px solid #1e293b',
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '1px', textTransform: 'uppercase' }}>Plan</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '1px', textTransform: 'uppercase' }}>Retention</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', letterSpacing: '1px', textTransform: 'uppercase' }}>Notes</div>
          </div>
          {RETENTION.map((row, i) => (
            <div
              key={row.plan}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px 1fr',
                padding: '14px 24px',
                borderBottom: i < RETENTION.length - 1 ? '1px solid #0f172a' : 'none',
                alignItems: 'center',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{row.plan}</div>
              <div style={{ fontSize: 13, color: '#E63946', fontWeight: 700 }}>{row.duration}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{row.notes}</div>
            </div>
          ))}
        </div>
      </div>

      {/* BAA / DPA CTA */}
      <div style={{ maxWidth: 740, margin: '0 auto 80px', padding: '0 40px' }}>
        <div
          style={{
            background: '#0d1117',
            border: '1px solid rgba(230,57,70,0.25)',
            borderRadius: 16,
            padding: '44px 40px',
            textAlign: 'center',
          }}
        >
          <h2 style={{ fontSize: 26, fontWeight: 900, marginBottom: 10, letterSpacing: '-0.5px' }}>
            Need a BAA, DPA, or security questionnaire filled?
          </h2>
          <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 28, lineHeight: 1.65 }}>
            Send us an email and we will return a signed HIPAA BAA or GDPR DPA within 24 hours.
            For security questionnaires (CAIQ, SIG, custom), allow 48 hours — we complete them in full.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="mailto:hello@guardrok.com?subject=BAA Request - GuardRok"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: '#E63946',
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                padding: '13px 24px',
                borderRadius: 10,
                textDecoration: 'none',
              }}
            >
              Request BAA <ArrowRight size={15} />
            </a>
            <a
              href="mailto:hello@guardrok.com?subject=GDPR DPA Request - GuardRok"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: '#0f172a',
                border: '1px solid #1e293b',
                color: '#fff',
                fontWeight: 600,
                fontSize: 14,
                padding: '13px 24px',
                borderRadius: 10,
                textDecoration: 'none',
              }}
            >
              Request DPA
            </a>
            <a
              href="mailto:privacy@guardrok.com?subject=Security Question"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: '#0f172a',
                border: '1px solid #1e293b',
                color: '#9ca3af',
                fontWeight: 600,
                fontSize: 14,
                padding: '13px 24px',
                borderRadius: 10,
                textDecoration: 'none',
              }}
            >
              Other questions
            </a>
          </div>
          <p style={{ fontSize: 11, color: '#374151', marginTop: 16 }}>
            privacy@guardrok.com · hello@guardrok.com · Response within 24 hours
          </p>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          borderTop: '1px solid #0f172a',
          padding: '24px 48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div style={{ fontSize: 13, color: '#374151' }}>© 2026 GuardRok™ · aiApas LLC</div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <Link to="/privacy" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Privacy</Link>
          <Link to="/terms" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Terms</Link>
          <Link to="/enterprise" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Enterprise</Link>
          <Link to="/pricing" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Pricing</Link>
          <a href="mailto:hello@guardrok.com" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>Contact</a>
        </div>
      </div>
    </div>
  )
}
